import React from 'react';
import { motion } from 'framer-motion';
import { 
  MessageSquare, 
  Bot, 
  Users, 
  Globe,
  Zap,
  Shield,
  CheckCircle2,
  ArrowRight,
  BrainCircuit,
  BarChart,
  Settings,
  Languages
} from 'lucide-react';
import { Link } from 'react-router-dom';

const features = [
  {
    icon: MessageSquare,
    title: 'Netspjall í rauntíma',
    description: 'Spjallaðu við viðskiptavini þína í rauntíma og veittu framúrskarandi þjónustu.'
  },
  {
    icon: Bot,
    title: 'AI spjallmenni',
    description: 'Sjálfvirkt spjallmenni sem svarar algengum spurningum allan sólarhringinn.'
  },
  {
    icon: Users,
    title: 'Teymissamvinna',
    description: 'Auðveld samvinna fyrir þjónustuteymi með skiptingu verkefna og samtala.'
  },
  {
    icon: Globe,
    title: 'Fjölmála stuðningur',
    description: 'Stuðningur við mörg tungumál fyrir alþjóðlega þjónustu.'
  },
  {
    icon: Zap,
    title: 'Hraðvirk þjónusta',
    description: 'Hröð og skilvirk samskipti við viðskiptavini.'
  },
  {
    icon: Shield,
    title: 'Örugg samskipti',
    description: 'Örugg og dulkóðuð samskipti fyrir trúnaðarupplýsingar.'
  }
];

const packages = [
  {
    name: 'Basic',
    price: '14.950 kr./mán',
    description: 'Fyrir minni fyrirtæki',
    features: [
      'Netspjall í rauntíma',
      'Einfalt spjallmenni',
      '1 þjónustufulltrúi',
      'Grunngreining á samtölum',
      'Skýrslur og tölfræði',
      'SSL dulkóðun',
      'Íslenskt viðmót',
      'Tæknilegur stuðningur'
    ]
  },
  {
    name: 'Business',
    price: '29.950 kr./mán',
    description: 'Fyrir meðalstór fyrirtæki',
    features: [
      'Allt í Basic pakka',
      'AI spjallmenni',
      '3 þjónustufulltrúar',
      'Ítarleg greining',
      'Sérsniðnar skýrslur',
      'Fjölmála stuðningur',
      'API tengingar',
      'Forgangsþjónusta',
      'Sérsniðið útlit',
      'CRM samþætting'
    ],
    popular: true
  },
  {
    name: 'Enterprise',
    price: '49.950 kr./mán',
    description: 'Fyrir stærri fyrirtæki',
    features: [
      'Allt í Business pakka',
      'Þróað AI spjallmenni',
      'Ótakmarkaðir þjónustufulltrúar',
      'Sérsniðin greiningartól',
      'Rauntímaeftirlit',
      'Öll helstu tungumál',
      'Sérsniðnar API lausnir',
      'VIP þjónusta',
      'Sérhönnun á viðmóti',
      'Heildstæð samþætting',
      'Öryggisúttektir',
      '24/7 stuðningur'
    ]
  }
];

const benefits = [
  {
    icon: BrainCircuit,
    title: 'Gervigreind',
    description: 'Nútímaleg AI tækni sem lærir og þróast með þinni þjónustu.'
  },
  {
    icon: BarChart,
    title: 'Greining',
    description: 'Ítarleg greining á samtölum og þjónustugæðum.'
  },
  {
    icon: Settings,
    title: 'Sérstilling',
    description: 'Sérsniðin lausn sem hentar þínum þörfum.'
  },
  {
    icon: Languages,
    title: 'Fjöltyngi',
    description: 'Stuðningur við öll helstu tungumál.'
  }
];

const LiveChat = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Netspjall með gervigreind
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Nútímalegt netspjall með gervigreind sem eykur þjónustugæði og 
              minnkar álag á starfsfólk.
            </p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                >
                  Fá tilboð í netspjall
                  <ArrowRight className="ml-2" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Öflug netspjallslausn
            </h2>
            <p className="text-xl text-gray-600">
              Allt sem þú þarft í nútímalegu netspjalli
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <feature.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Packages Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Netspjallspakkar
            </h2>
            <p className="text-xl text-gray-600">
              Veldu þann pakka sem hentar þínum þörfum
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div 
                key={index}
                className={`bg-white rounded-xl p-8 shadow-sm border ${
                  pkg.popular ? 'border-[#EA295A]' : 'border-gray-100'
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                {pkg.popular && (
                  <div className="bg-[#EA295A]/10 text-[#EA295A] text-sm font-medium px-3 py-1 rounded-full inline-block mb-4">
                    Vinsælast
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <div className="text-3xl font-bold text-[#EA295A] mb-1">{pkg.price}</div>
                <p className="text-gray-500 text-sm mb-4">verð án VSK</p>
                <p className="text-gray-600 mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full px-6 py-3 rounded-lg font-medium flex items-center justify-center ${
                      pkg.popular
                        ? 'bg-[#EA295A] text-white hover:bg-[#d01d4c]'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    } transition-all`}
                  >
                    Velja pakka
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </motion.button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Benefits Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Kostir netspjalls
            </h2>
            <p className="text-xl text-gray-600">
              Helstu kostir þess að nota okkar netspjallslausn
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <benefit.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
            <motion.h2 
              className="text-3xl font-bold text-white mb-6"
            >
              Tilbúin að bæta þjónustuna?
            </motion.h2>
            <motion.p 
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              Láttu okkur hjálpa þér að setja upp netspjall sem eykur þjónustugæði 
              og ánægju viðskiptavina
            </motion.p>
            <motion.div>
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                >
                  Fá tilboð í netspjall
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LiveChat;